import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react'
import { Card, Tag, Text } from '@grovus/design-system'
import { SessionSettings } from '../components/SessionSettings'
import { useNarrow } from '../components/useNarrow'
import { ADMIN_COPY, type Dict, type Lang } from '../i18n'
import type { Theme } from '../lib/preferences'
import { fetchAdminUsers, type AdminUser } from './api'
import { CompaniesPage } from './CompaniesPage'
import { PortfoliosPage } from './PortfoliosPage'
import { RequestsPage, useAccessRequests } from './RequestsPage'
import { isAdminPath, sectionOf, sectionPath, type Section } from './sections'
import { UsersPage, type UsersView } from './UsersPage'

interface Props {
  t: Dict
  lang: Lang
  setLang: (lang: Lang) => void
  theme: Theme
  toggleTheme: () => void
  userId: string
  /** The list the boundary loaded on entry, or replaced on its focus recheck. */
  users: AdminUser[]
  onSignOut: () => Promise<'ok' | 'error'>
  /** The boundary's `leaveAdmin()`: every `denied` answer, and a Back out of `/admin`, lands here. */
  onLeave: () => void
}

const BORDER = '1px solid var(--mantine-color-default-border)'

/**
 * The platform admin panel (screens 11 to 14): wordmark with the "Admin" tag, the user
 * menu, and the admin navigation. Each section has its URL under `/admin` (GRO-188): a
 * click pushes one history entry, Back and Forward move between sections, and a bare or
 * unknown path opens Users.
 */
export function AdminScreen({ t, lang, setLang, theme, toggleTheme, userId, users, onSignOut, onLeave }: Props) {
  const copy = ADMIN_COPY[lang]
  const narrow = useNarrow()
  const [section, setSection] = useState<Section>(() => sectionOf(window.location.pathname) ?? 'users')
  const [usersView, setUsersView] = useState<UsersView>({ status: 'ok', users })
  const [menuOpen, setMenuOpen] = useState(false)
  const [signOut, setSignOut] = useState<'idle' | 'pending' | 'failed'>('idle')
  /** A company rename is pending: the sections stay put until it settles (GRO-136, D5). */
  const [locked, setLocked] = useState(false)
  const attachedRef = useRef(false)
  const requests = useAccessRequests(onLeave)

  useEffect(() => { setUsersView({ status: 'ok', users }) }, [users])

  const reloadUsers = useCallback(async () => {
    setUsersView({ status: 'loading' })
    const result = await fetchAdminUsers()
    if (result.status === 'ok') setUsersView({ status: 'ok', users: result.body })
    else if (result.status === 'denied') onLeave()
    else if (result.status !== 'stale' && result.status !== 'expired') setUsersView({ status: 'failed' })
  }, [onLeave])

  const { reload: reloadRequests } = requests
  const show = useCallback((next: Section) => {
    setSection(next)
    if (next === 'requests') reloadRequests()
    else if (next === 'users') void reloadUsers()
  }, [reloadRequests, reloadUsers])

  function select(next: Section) {
    if (locked || next === section) return
    window.history.pushState({ grovusAdmin: true }, '', sectionPath(next))
    show(next)
  }

  // Back and Forward. The URL has already moved when this runs, so a pending rename puts
  // the current section back rather than refusing (GRO-136, D5).
  useEffect(() => {
    function onPopState() {
      const pathname = window.location.pathname
      if (locked) {
        window.history.pushState({ grovusAdmin: true }, '', sectionPath(section))
        return
      }
      if (!isAdminPath(pathname)) {
        onLeave()
        return
      }
      const next = sectionOf(pathname) ?? 'users'
      if (sectionOf(pathname) === null) window.history.replaceState({ grovusAdmin: true }, '', sectionPath(next))
      if (next !== section) show(next)
    }
    window.addEventListener('popstate', onPopState)
    // The boundary writes the panel's history before it renders the panel, so a non-admin
    // path at the first attach is a Back that came before this listener existed.
    if (!attachedRef.current) {
      attachedRef.current = true
      if (!isAdminPath(window.location.pathname)) onPopState()
    }
    return () => window.removeEventListener('popstate', onPopState)
  }, [locked, section, show, onLeave])

  async function handleSignOut() {
    setSignOut('pending')
    const result = await onSignOut()
    if (result === 'error') setSignOut('failed')
  }

  const me = (usersView.status === 'ok' ? usersView.users : users).find((u) => u.id === userId)?.email ?? ''
  const pendingCount = requests.lists.status === 'ok' ? requests.lists.pending.length : 0

  const navItem = (value: Section, label: string, count?: number) => (
    <button
      type="button"
      className="grv-nav-item"
      data-active={section === value || undefined}
      aria-current={section === value ? 'page' : undefined}
      aria-disabled={locked || undefined}
      onClick={() => select(value)}
    >
      <span>{label}</span>
      {count ? <span className="grv-nav-count">{count}</span> : null}
    </button>
  )

  const avatarPart = (style: CSSProperties) => (
    <span style={{ position: 'absolute', left: '50%', borderRadius: 'var(--mantine-radius-xl)', background: 'light-dark(var(--mantine-color-ink-5), var(--mantine-color-dark-2))', ...style }} />
  )

  return (
    <div data-testid="admin-screen" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'var(--mantine-color-body)', color: 'var(--mantine-color-text)' }}>
      <header style={{ position: 'relative', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, padding: narrow ? '12px 16px' : '12px 32px', borderBottom: '2px solid light-dark(var(--mantine-color-ink-1), var(--mantine-color-dark-5))' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 10px', marginRight: narrow ? 0 : 16, flex: narrow ? '1 1 0' : '0 0 auto', minWidth: 0 }}>
          <span style={{ fontSize: 'var(--mantine-font-size-xl)', fontWeight: 700, letterSpacing: '0.04em' }}>GROVUS</span>
          <Tag>{copy.admin}</Tag>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <span style={{ fontSize: narrow ? 'var(--mantine-font-size-xs)' : 'var(--mantine-font-size-sm)', fontWeight: 500, overflowWrap: 'anywhere', textAlign: 'right' }}>{me}</span>
          <button
            type="button"
            aria-label={copy.account}
            aria-expanded={menuOpen}
            data-testid="admin-menu-toggle"
            onClick={() => setMenuOpen((open) => !open)}
            style={{ position: 'relative', width: '2.25rem', height: '2.25rem', flex: 'none', padding: 0, cursor: 'pointer', borderRadius: 'var(--mantine-radius-xl)', border: BORDER, background: 'light-dark(var(--mantine-color-ink-1), var(--mantine-color-dark-6))', overflow: 'hidden' }}
          >
            {avatarPart({ top: 7, width: 10, height: 10, marginLeft: -5 })}
            {avatarPart({ top: 20, width: 20, height: 14, marginLeft: -10 })}
          </button>
        </div>
        {menuOpen && (
          <Card role="group" aria-label={t.userMenu} style={{ position: 'absolute', top: '100%', right: narrow ? 16 : 32, zIndex: 10, width: 300 }}>
            <Text fw={600} fz="sm" style={{ overflowWrap: 'anywhere' }}>{me}</Text>
            <SessionSettings
              t={t} lang={lang} setLang={setLang} theme={theme} toggleTheme={toggleTheme}
              onSignOut={handleSignOut} signOutPending={signOut === 'pending'}
            />
            {signOut === 'failed' && (
              <Text fz="sm" mt="sm" role="alert" style={{ color: 'light-dark(var(--mantine-color-bad-7), var(--mantine-color-bad-3))' }}>{t.authSignOutFailed}</Text>
            )}
          </Card>
        )}
      </header>

      <div style={{ display: 'grid', gridTemplateColumns: narrow ? 'minmax(0, 1fr)' : '220px minmax(0, 1fr)', flex: 1, alignItems: 'stretch' }}>
        <aside style={{ display: 'flex', flexDirection: 'column', gap: 24, padding: narrow ? 16 : '16px 12px', borderRight: narrow ? 'none' : BORDER, borderBottom: narrow ? BORDER : 'none' }}>
          {narrow && <span className="grv-label">{copy.menu}</span>}
          <nav className="grv-nav" aria-label={copy.navLabel}>
            {navItem('users', copy.navUsers)}
            {navItem('companies', copy.navCompanies)}
            {navItem('portfolios', copy.navPortfolios)}
            {navItem('requests', copy.requestsTitle, pendingCount)}
          </nav>
        </aside>
        <main style={{ display: 'flex', flexDirection: 'column', gap: section === 'requests' ? 32 : 24, minWidth: 0, padding: narrow ? '20px 16px 32px' : '28px 32px 48px' }}>
          {section === 'users' && <UsersPage copy={copy} view={usersView} narrow={narrow} onRetry={() => void reloadUsers()} />}
          {section === 'companies' && <CompaniesPage copy={copy} narrow={narrow} onLeave={onLeave} onPending={setLocked} />}
          {section === 'portfolios' && <PortfoliosPage copy={copy} narrow={narrow} onLeave={onLeave} />}
          {section === 'requests' && <RequestsPage copy={copy} narrow={narrow} requests={requests} />}
        </main>
      </div>
    </div>
  )
}
